import {
  deserify,
  IconWrapper,
  InputField,
  InputFieldContainer,
  IStyles,
  pxToRem,
  pxTovW,
  QuantityButton,
  SelectMenu,
  theme,
} from '@geneo2-web/shared-ui';
import { Checkbox, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { Question_QuestionEnum } from '@protos/content_management/content.db_pb';
import {
  QuestionSection,
  QuestionTypeMarks,
} from '@protos/learning_management/lms.db_pb';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import { useEffect } from 'react';
import { numberToAlphabet } from '../../ReviewQuestionPaper/utils';
import { QuestionTypeCount } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../../Auth/auth.events';
import { useDispatch } from 'react-redux';
import {
  setQPGQuestionTypeMarks,
  setQPGSettingsConfig,
} from '../../../reducer/assessmentFlow.slice';

const style: IStyles = {
  root: {
    boxSizing: 'border-box',
    width: '100%',
    // height: { xs: pxToRem(200), md: pxTovW(310) },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: { xs: pxToRem(15), md: pxTovW(30) },
    border: '1px solid #007CDC',
    backgroundColor: '#fff',
    padding: { xs: pxToRem(20), md: pxTovW(40) },
  },
};

interface ISectionProps {
  index: number;
  section: QuestionSection;
  updateSection: (index: number, section: QuestionSection) => void;
  deleteSection: (index: number) => void;
}
interface IOptionList {
  name: string;
  nonEditableItem?: boolean;
  marks?: number;
  noOfQuestions?: number;
  questionType?: Question_QuestionEnum;
}

const MAX_AI_QUESTIONS = 25;

export const SectionComp = (props: ISectionProps) => {
  const { index, section, updateSection, deleteSection } = props;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  // const [sectionSettings, setSectionSettings] = useState<QuestionSection>(
  //   section || new QuestionSection({})
  // );
  const { qpg_settings_config, current_qpg_template_info } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const ismobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();
  function getMaxQuestionCountByEnum(
    enumValue?: Question_QuestionEnum
  ): number {
    if (!questionTypeList.length || !enumValue) {
      return 0;
    }
    const questionTypeFound = questionTypeList.find(
      (e) => e.questionType === enumValue
    );
    return questionTypeFound?.noOfQuestions || 0;
  }

  const noOfQuestions = new QuestionTypeCount(
    qpg_settings_config?.no_ofQuestions_by_types
  );
  const {
    noOfMcqs,
    noOfMcqm,
    noOfFib,
    noOfTf,
    noOfVeryShort,
    noOfShort,
    noOfLong,
    noOfCbq,
  } = noOfQuestions;

  const getNoOfQuestions = (number?: number) => {
    return qpg_settings_config?.allow_ai_questions
      ? MAX_AI_QUESTIONS
      : number || 0;
  };

  const questionTypeList: IOptionList[] = [
    {
      name: 'MCQ Single',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqs),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQS,
    },
    {
      name: 'MCQ Multiple',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqm),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQM,
    },
    {
      name: 'Fill in the Blanks',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfFib),
      questionType: Question_QuestionEnum.QUESTION_TYPE_FIB,
    },
    {
      name: 'True/False',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfTf),
      questionType: Question_QuestionEnum.QUESTION_TYPE_TF,
    },
    {
      name: 'Very Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfVeryShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
    },
    {
      name: 'Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_SHORT,
    },
    {
      name: 'Long Answer',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfLong),
      questionType: Question_QuestionEnum.QUESTION_TYPE_LONG,
    },

    {
      name: 'Competency Based',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfCbq),
      questionType: Question_QuestionEnum.QUESTION_TYPE_CBQ,
    },
  ].filter((e) => e.noOfQuestions && e.noOfQuestions > 0);

  const updateQuestionTypeMarks = (
    questionType: Question_QuestionEnum,
    marks: number
  ) => {
    const updatedQuestionTypeMarks = new QuestionTypeMarks({
      questionType: questionType,
      marks: marks,
    });
    const updatedQuestionTypeMarksList =
      qpg_settings_config?.question_type_marks?.filter(
        (e) => e.questionType !== questionType
      ) || [];
    updatedQuestionTypeMarksList.push(updatedQuestionTypeMarks);
    dispatch(
      setQPGSettingsConfig({
        ...qpg_settings_config,
        question_type_marks: updatedQuestionTypeMarksList,
      })
    );
  };

  return (
    <Box sx={style.root}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: pxToRem(10), md: pxTovW(20) },
            alignItems: 'center',
          }}
        >
          <IconWrapper
            name="reorder"
            type="png"
            parentFolder="icons"
            customSx={{
              height: { xs: pxToRem(20), md: pxTovW(37) },
              width: { xs: pxToRem(15), md: pxTovW(23) },
              cursor: 'grab',
            }}
          />
          <Typography
            variant={ismobile ? 'h2' : 'h1'}
            fontWeight="bold"
          >{`Section ${numberToAlphabet(index + 1)}`}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: pxToRem(10), md: pxTovW(20) },
            alignItems: 'flex-start',
          }}
        >
          {section.noOfQuestion > 0 && (
            <Box
              sx={{
                width: { xs: pxToRem(80), md: pxTovW(130) },
                height: { xs: pxToRem(30), md: pxTovW(43) },
                borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                backgroundColor:
                  section.noOfQuestion > 0 ? '#DDFAEC' : '#F2F2F2',
                borderBottom: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="cardText">
                {(section.noOfQuestion || 0) * (section.marks || 0)} Marks
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              cursor: 'pointer',
              height: { xs: pxToRem(20), md: pxTovW(43) },
              width: { xs: pxToRem(20), md: pxTovW(43) },
            }}
          >
            <IconWrapper
              name="delete2"
              type="png"
              parentFolder="icons"
              onClick={async () => {
                deleteSection(index);
                updateQuestionTypeMarks(section.questionType, 1);
                await interactionEvent({
                  context: 'Section',
                  name: 'DELETE',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: pxToRem(10), md: pxTovW(10) },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', lg: 'center' },
          mt: { xs: pxToRem(20), md: pxTovW(40) },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: pxToRem(10), md: pxTovW(20) },
            justifyContent: 'space-around',
            alignItems: { md: 'center' },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 'unset' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: { xs: pxToRem(10), md: pxToRem(20) },
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              Choose Question Type
            </Typography>
            <SelectMenu
              parentStyles={{ width: { xs: '100%', md: 'unset' } }}
              placeholder="Select Question Type"
              value={
                questionTypeList.find(
                  (e) => e.questionType === section.questionType
                )?.name || ''
              }
              renderValue={(selected, placeholder) => {
                const questionTypeFound = questionTypeList.find(
                  (e) => e.name === selected
                );
                return !questionTypeFound ? (
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 'bold',
                      textAlign: 'left',
                      fontSize: { xs: pxToRem(14), md: pxTovW(14) },
                      fontFamily: 'Poppins',
                    }}
                  >
                    {placeholder}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 'bold',
                      textAlign: 'left',
                      fontSize: { xs: pxToRem(14), md: pxTovW(14) },
                      fontFamily: 'Poppins',
                    }}
                  >
                    {questionTypeFound.name}
                    <Typography
                      variant="cardText"
                      sx={{
                        color: '#0078D5',
                        pl: { xs: pxToRem(6), md: pxTovW(6) },
                      }}
                    >
                      {questionTypeFound.marks} Marks
                    </Typography>
                  </Box>
                );
              }}
              optionList={
                qpg_settings_config?.allow_ai_questions
                  ? questionTypeList
                      .map((e, index) => ({
                        name: e.name,
                        id: index + 1,
                        nonEditableItem: e.nonEditableItem,
                      }))
                      .filter((e) => e.name !== 'Competency Based')
                  : questionTypeList.map((e, index) => ({
                      name: e.name,
                      id: index + 1,
                      nonEditableItem: e.nonEditableItem,
                    }))
              }
              onChange={async (value: string) => {
                const questionTypeFound = questionTypeList.find(
                  (e) => e.name === value
                );
                if (questionTypeFound) {
                  const updatedQuestionSection = new QuestionSection({
                    ...section,
                    noOfQuestion: 0,
                    marks: questionTypeFound.marks || 0,
                    questionType:
                      questionTypeFound.questionType ||
                      Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
                  });
                  updateSection(index, updatedQuestionSection);
                }
                await interactionEvent({
                  context: 'Select_question_type',
                  name: value
                    .toUpperCase()
                    .replace(/\s+/g, '_')
                    .replace(/\//g, '_/_'),
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              rootStyles={{
                height: { xs: pxToRem(40), md: pxTovW(70), lg: pxTovW(56) },
                backgroundColor: '#F5FAFF',
                border: '1px solid #55ACFF',
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:focus-visible .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select > em': {
                  // backgroundColor: 'red',
                  fontStyle: 'normal !important',
                  fontFamily: 'Lato',
                },
                // borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                // width: { xs: '100%', md: pxTovW(326) },
                width: { xs: '100%', md: pxTovW(206) },
              }}
            />
          </Box>
          <Box
            sx={{
              width: { xs: '100%', lg: 'unset' },
              display: 'flex',
              gap: { xs: pxToRem(10), md: pxTovW(20) },
              justifyContent: 'space-around',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: { xs: pxToRem(131), md: pxTovW(180) },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: { xs: pxToRem(10), md: pxTovW(20) },
                // alignItems: { md: 'center' },
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                marginRight={'auto'}
                width={'max-content'}
              >
                Add Marks
              </Typography>
              <QuantityButton
                withBorder
                smallVariant={isIpad}
                quantity={
                  section.marks ||
                  qpg_settings_config?.question_type_marks?.find(
                    (e) => e.questionType === section.questionType
                  )?.marks ||
                  0
                }
                maxQuantity={
                  section.questionType ===
                  Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
                    ? 0
                    : 20
                }
                incrementClickHandler={async () => {
                  const currentMarks = section.marks ?? 0;
                  const newMarks = currentMarks + 1;
                  const updatedQuestionSection = new QuestionSection({
                    ...section,
                    noOfQuestion: section.noOfQuestion,
                    marks: newMarks,
                  });
                  updateSection(index, updatedQuestionSection);
                  updateQuestionTypeMarks(section.questionType, newMarks);
                }}
                decrementClickHandler={async () => {
                  const currentMarks = section.marks ?? 0;
                  const newMarks = Math.max(0, currentMarks - 1);
                  const updatedQuestionSection = new QuestionSection({
                    ...section,
                    noOfQuestion: section.noOfQuestion,
                    marks: newMarks,
                  });
                  updateSection(index, updatedQuestionSection);
                  updateQuestionTypeMarks(section.questionType, newMarks);
                }}
              />
            </Box>
            <Box
              sx={{
                width: { xs: pxToRem(131), md: pxTovW(180) },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: { xs: pxToRem(10), md: pxTovW(20) },
                // alignItems: { md: 'center' },
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                marginRight={'auto'}
                width={'max-content'}
              >
                No. of Questions
              </Typography>
              <QuantityButton
                withBorder
                smallVariant={isIpad}
                quantity={section.noOfQuestion}
                maxQuantity={getMaxQuestionCountByEnum(section.questionType)}
                incrementClickHandler={async () => {
                  if (
                    section.noOfQuestion <
                    getMaxQuestionCountByEnum(section.questionType)
                  ) {
                    const updatedQuestionSection = new QuestionSection({
                      ...section,
                      noOfQuestion: section.noOfQuestion + 1,
                      marks:
                        section.marks ||
                        qpg_settings_config?.question_type_marks?.find(
                          (e) => e.questionType === section.questionType
                        )?.marks ||
                        0,
                    });
                    updateSection(index, updatedQuestionSection);
                  }
                  await interactionEvent({
                    context: 'Number_of_questions',
                    name: 'ADD',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }}
                decrementClickHandler={async () => {
                  if (section.noOfQuestion > 0) {
                    const updatedQuestionSection = new QuestionSection({
                      ...section,
                      noOfQuestion: section.noOfQuestion - 1,
                    });
                    updateSection(index, updatedQuestionSection);
                  }
                  await interactionEvent({
                    context: 'Number_of_questions',
                    name: 'REMOVE',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: pxToRem(10), md: pxTovW(10) },
            marginTop: { xs: pxToRem(20), md: pxTovW(40) },
            cursor: 'pointer',
          }}
        >
          <Checkbox
            checked={section.isAlternateEnable}
            onChange={async (e) => {
              const updatedQuestionSection = new QuestionSection({
                ...section,
                isAlternateEnable: e.target.checked,
              });
              updateSection(index, updatedQuestionSection);
              await interactionEvent({
                context: 'Add_alternate_questions',
                name: e.target.checked ? 'SELECTED' : 'UNSELECTED',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            sx={{
              height: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
              padding: { xs: pxToRem(5), md: pxTovW(8), lg: pxTovW(5) },
              width: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
              borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
            }}
            color="primary"
          />
          <Typography
            variant="h4"
            sx={{
              width: 'max-content',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Add Alternate Questions
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="linkText"
        color="primary"
        textAlign="center"
        onClick={() => {
          //
        }}
        sx={{
          marginTop: { xs: pxToRem(10), md: pxTovW(20) },
          fontFamily: 'poppins',
          fontSize: { xs: pxToRem(21), md: pxTovW(21) },
          // cursor: idAddMoreDisabled() ? 'not-allowed' : 'pointer',
          // color: idAddMoreDisabled() ? 'grey' : 'primary',
        }}
      >
        + Add More
      </Typography>
    </Box>
  );
};
